import { CallStatus, ChannelType } from 'src/api/generated';
import { format } from 'date-fns';
import { PiPhoneIncoming, PiPhoneOutgoing } from 'react-icons/pi';
import { StyledIcon } from 'src/modules/shared/components/atoms/icons/styled-icon';
import { formatFullName } from 'src/modules/shared/utils/formatting/name.util';
import { formatPhoneNumber } from 'src/modules/shared/utils/formatting/phone.util';
import { nonEmptyString } from 'src/modules/shared/utils/formatting/non-empty-string.util';
import { CallStatusIndicator } from 'src/modules/shared/components/atoms/indicators/call-status-indicator';
import {
  useCurrentUser,
  useUser,
} from 'src/modules/shared/hooks/api/admin/users';
import { TextSkeleton } from 'src/modules/shared/components/atoms/text/text-skeleton';
import { Text } from 'src/modules/shared/components/atoms/text';
import { classes } from 'src/modules/shared/utils/styles.util';
import { TimelineItemActions } from './timeline-item-actions';
import { TimelineItemDetails } from './timeline-item-details';
import { TimelineItemShell } from './timeline-item-shell';
import { TimelineContextBadges } from './timeline-context-badges';
import { TimelineCallDetails } from './timeline-call-details';
import type { TimelineCallItemProps } from '../../../types/timeline/compact-timeline.types';
import {
  getContextBorderTintClass,
  getContextOpacityClass,
} from '../../../utils/timeline/item-classes.util';

/** Always-visible call content, with one active player across the timeline. */
export function TimelineCallItem({
  item,
  contact,
  onPlayRecording,
  activeCallId,
  context,
}: TimelineCallItemProps) {
  const userId = item.userId ? Number(item.userId) : null;
  const { user, loading: userLoading } = useUser(userId ?? -1, userId !== null);
  const { user: currentUser, loading: currentUserLoading } = useCurrentUser();
  const outbound = item.direction === 'outgoing';
  const completed = item.status === CallStatus.COMPLETED;
  const name = !outbound
    ? formatFullName(
        contact?.firstName,
        contact?.lastName,
        formatPhoneNumber(item.from)
      )
    : currentUser && item.userId === String(currentUser.id)
      ? 'Tú'
      : user
        ? formatFullName(user.firstName, user.lastName, user.email)
        : 'Usuario';
  const directionLabel = outbound ? 'Llamada saliente' : 'Llamada entrante';
  const DirectionIcon = outbound ? PiPhoneOutgoing : PiPhoneIncoming;
  return (
    <TimelineItemShell
      initial={false}
      className={classes(
        'w-full max-w-[92%] sm:max-w-md rounded-xl space-y-1.5 [@media(hover:hover)_and_(pointer:fine)]:max-w-[min(28rem,calc(100%_-_2rem))]',
        outbound ? 'ml-auto bg-call-50 border-call-100' : 'mr-auto',
        getContextBorderTintClass(context),
        getContextOpacityClass(context)
      )}
    >
      <TimelineItemActions
        label={`Opciones de ${directionLabel.toLowerCase()}, ${format(new Date(item.createdAt), 'HH:mm')}`}
        menuSide={outbound ? 'left' : 'right'}
        menuVariant={outbound ? 'call' : 'neutral'}
        details={
          <TimelineItemDetails
            channel={ChannelType.PHONE}
            senderAddress={item.from}
            recipientAddress={item.to}
            redirectedTo={nonEmptyString(item.redirectedTo)}
            description={`${name} · ${format(new Date(item.createdAt), "PP 'a las' HH:mm")}`}
          />
        }
      >
        <div className="flex items-start gap-2 min-w-0">
          <StyledIcon
            icon={<DirectionIcon role="img" aria-label={directionLabel} />}
            variant="call"
            size="sm"
            className="mt-0.5 shrink-0"
          />
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-baseline justify-between gap-3">
              {userLoading || currentUserLoading ? (
                <TextSkeleton className="h-4 w-20" />
              ) : (
                <Text
                  size="small"
                  weight="semibold"
                  className="min-w-0 truncate"
                  title={name}
                >
                  {name}
                </Text>
              )}
              <Text size="tiny" color="secondary" className="shrink-0">
                {format(new Date(item.createdAt), 'HH:mm')}
              </Text>
            </div>
            <div className="flex items-center gap-1.5 mt-1 whitespace-nowrap">
              <Text
                size="tiny"
                color="secondary"
                className="min-w-0 truncate"
                title={directionLabel}
              >
                {directionLabel}
              </Text>
              <Text as="span" size="tiny" color="secondary">
                ·
              </Text>
              <CallStatusIndicator
                status={item.status}
                duration={item.duration || undefined}
                size="xs"
                showIcons={false}
                className="shrink-0"
              />
            </div>
          </div>
        </div>
        <TimelineContextBadges context={context} />
        {completed && (
          <TimelineCallDetails
            item={item}
            activeCallId={activeCallId}
            onPlayRecording={onPlayRecording}
          />
        )}
      </TimelineItemActions>
    </TimelineItemShell>
  );
}
